import { useEffect, useState } from "react";
import { Apple, Download, Monitor, Plus, Share2, Smartphone, X } from "lucide-react";
import { requestPwaInstall } from "../utils/pwaInstall";

function isIosDevice() {
  const userAgent = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function AppDownload() {
  const [installPromptAvailable, setInstallPromptAvailable] = useState(
    () => typeof window !== "undefined" && !!window.__lefteriaInstallPrompt
  );
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  useEffect(() => {
    const onAvailable = () => setInstallPromptAvailable(true);
    const onConsumed = () => setInstallPromptAvailable(false);
    const onInstalled = () => {
      setInstallPromptAvailable(false);
      setShowInstallHelp(false);
    };
    window.addEventListener("lefteria-install-available", onAvailable);
    window.addEventListener("lefteria-install-prompt-consumed", onConsumed);
    window.addEventListener("lefteria-install-completed", onInstalled);
    return () => {
      window.removeEventListener("lefteria-install-available", onAvailable);
      window.removeEventListener("lefteria-install-prompt-consumed", onConsumed);
      window.removeEventListener("lefteria-install-completed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const result = await requestPwaInstall();
    if (result.shown) {
      setInstallPromptAvailable(false);
      return;
    }

    setShowInstallHelp(true);
  };

  return (
    <>
      <div className="mt-10 border-t border-[#262626] pt-8" data-testid="footer-app-download">
        <h4 className="mb-3 font-['Bebas_Neue'] text-xl tracking-wider text-[#F5A623]">
          Download Our Official App
        </h4>
        <p className="mb-4 max-w-md text-sm leading-relaxed text-zinc-500">
          One official Lefteria FC app for iPhone, iPad, Android, and desktop. Follow matches, news, moments, polls, and club notifications.
        </p>
        <button
          type="button"
          onClick={handleInstall}
          aria-label="Install the Lefteria FC app"
          data-testid="footer-download-app"
          className="group inline-flex min-h-[44px] items-center gap-3 border border-[#262626] bg-black px-5 py-3 text-white transition-colors hover:border-[#F5A623] hover:text-[#F5A623]"
        >
          <Download size={21} aria-hidden="true" />
          <span className="font-semibold">Install Our Official App</span>
        </button>
        {!installPromptAvailable && (
          <p className="mt-2 max-w-md text-xs leading-relaxed text-zinc-600">
            Opens the install option supported by your device and browser.
          </p>
        )}
      </div>

      {showInstallHelp && (
        <InstallHelpModal onClose={() => setShowInstallHelp(false)} />
      )}
    </>
  );
}

function InstallHelpModal({ onClose }) {
  const ios = isIosDevice();
  const android = /Android/i.test(navigator.userAgent || "");
  const title = ios
    ? "Εγκατάσταση σε iPhone ή iPad"
    : android
      ? "Εγκατάσταση στο Android"
      : "Install the Lefteria FC app";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 px-4 pb-4 backdrop-blur-sm md:items-center md:pb-0"
      onClick={onClose}
      data-testid="pwa-install-help"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-title"
        className="w-full rounded-2xl border border-[#262626] bg-[#121212] shadow-2xl md:max-w-md"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#262626] px-5 py-4">
          <div className="flex items-center gap-2.5">
            {ios ? <Apple size={22} className="text-white" /> : android ? <Smartphone size={22} className="text-[#F5A623]" /> : <Monitor size={22} className="text-[#F5A623]" />}
            <h3 id="pwa-install-title" className="font-semibold tracking-wide text-white">{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Κλείσιμο" className="text-zinc-400 hover:text-white" data-testid="pwa-install-close">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {ios ? (
            <>
              <p className="text-sm leading-relaxed text-zinc-400">Το iPhone δεν επιτρέπει σε ιστοσελίδες να ξεκινήσουν την εγκατάσταση αυτόματα. Άνοιξε αυτή τη σελίδα στο Safari και ολοκλήρωσε την προσθήκη από το μενού του.</p>
              <InstallStep n="1" icon={<Share2 size={16} />} title="Άνοιξε το Safari" body="Αν βλέπεις την εφαρμογή μέσα από άλλη εφαρμογή, άνοιξε πρώτα τη σελίδα στο Safari." />
              <InstallStep n="2" icon={<Share2 size={16} />} title="Κοινοποίηση" body="Πάτησε το κουμπί Κοινοποίηση στο κάτω μέρος του Safari (ή στην επάνω γραμμή σε iPad)." />
              <InstallStep n="3" icon={<Plus size={16} />} title="Προσθήκη στην οθόνη Αφετηρίας" body="Επίλεξε «Προσθήκη στην οθόνη Αφετηρίας» και επιβεβαίωσε με «Προσθήκη»." />
            </>
          ) : android ? (
            <>
              <p className="text-sm leading-relaxed text-zinc-400">Αν δεν εμφανίστηκε το παράθυρο εγκατάστασης, άνοιξε το μενού του browser.</p>
              <InstallStep n="1" icon={<Smartphone size={16} />} title="Άνοιξε το μενού του browser" body="Στο Chrome πάτησε ⋮ και επίλεξε «Εγκατάσταση εφαρμογής» ή «Προσθήκη στην αρχική οθόνη»." />
              <InstallStep n="2" icon={<Plus size={16} />} title="Επιβεβαίωσε" body="Πάτησε «Εγκατάσταση» ή «Προσθήκη» για να βάλεις την εφαρμογή στην αρχική οθόνη." />
            </>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-zinc-400">Το κουμπί εγκατάστασης διαφέρει ανά browser. Άνοιξε το μενού του browser ή χρησιμοποίησε το εικονίδιο εγκατάστασης στη γραμμή διεύθυνσης.</p>
              <InstallStep n="1" icon={<Monitor size={16} />} title="Install app / Add to Home Screen" body="Επίλεξε την εγκατάσταση της εφαρμογής και επιβεβαίωσε στο παράθυρο του browser." />
            </>
          )}

          <a href="/app" className="block rounded-lg bg-[#F5A623] py-3 text-center text-sm font-semibold text-black transition-colors hover:bg-[#e09620]">
            Άνοιξε την εφαρμογή στο browser
          </a>
        </div>
      </section>
    </div>
  );
}

function InstallStep({ n, icon, title, body }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#F5A623]/30 bg-[#F5A623]/15 text-sm font-bold text-[#F5A623]">
        {n}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
          <span className="text-[#F5A623]">{icon}</span>{title}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-zinc-400">{body}</p>
      </div>
    </div>
  );
}

